import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Nome della schermata, per dire DOVE si è rotto. */
  area?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Rete di sicurezza contro la pagina bianca.
 *
 * Senza questo, un payload di forma inattesa (es. un oggetto dove il codice
 * si aspetta una lista) fa esplodere il render e React smonta l'intero
 * albero: l'utente vede una schermata vuota, senza un solo indizio su cosa
 * sia successo. Su un'app che scrive prezzi, "vuoto" è indistinguibile da
 * "non c'è niente da mostrare".
 *
 * Deve essere una classe: React non espone ancora `componentDidCatch` agli
 * hook.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console -- unico canale diagnostico lato browser
    console.error("[ErrorBoundary]", this.props.area ?? "app", error, info);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div role="alert" className="p-8">
        <div className="mx-auto max-w-xl rounded-[var(--radius-lg)] border border-destructive/30 bg-destructive/5 p-6">
          <h1 className="page-title">
            Questa schermata si è rotta
          </h1>
          <p className="mt-2 text-sm text-ink-2">
            {this.props.area ? `Area: ${this.props.area}. ` : ""}
            Il resto dell&apos;applicazione continua a funzionare, e nessun dato è stato modificato
            da questo errore.
          </p>
          <pre className="mt-4 overflow-x-auto rounded-md bg-paper-2 p-3 font-mono text-label text-destructive">
            {error.message}
          </pre>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => this.setState({ error: null })}
            >
              Riprova
            </button>
            <button type="button" className="btn" onClick={() => window.location.reload()}>
              Ricarica la pagina
            </button>
          </div>
        </div>
      </div>
    );
  }
}
